import React, { useState, useEffect, useRef } from 'react';
import '../App.css';

function CameraView({ socket }) {
  const [streamEnabled, setStreamEnabled] = useState(false);
  const [cameraRunning, setCameraRunning] = useState(true); // Camera starts automatically
  const canvasRef = useRef(null);
  const [fps, setFps] = useState(0);
  const frameCountRef = useRef(0);
  const lastFpsTimeRef = useRef(Date.now());

  useEffect(() => {
    if (!socket) return;

    const handleVideoFrame = (data) => {
      if (canvasRef.current && data.frame) {
        const img = new Image();
        img.onload = () => {
          const canvas = canvasRef.current;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          
          // Calculate FPS
          frameCountRef.current++;
          const now = Date.now();
          if (now - lastFpsTimeRef.current >= 1000) {
            setFps(frameCountRef.current);
            frameCountRef.current = 0;
            lastFpsTimeRef.current = now;
          }
        };
        img.src = 'data:image/jpeg;base64,' + data.frame;
      }
    };

    socket.on('video_frame', handleVideoFrame);
    socket.on('stream_enabled', () => setStreamEnabled(true));
    socket.on('stream_disabled', () => setStreamEnabled(false));
    socket.on('camera_started', () => setCameraRunning(true));
    socket.on('camera_stopped', () => setCameraRunning(false));

    return () => {
      socket.off('video_frame', handleVideoFrame);
      socket.off('stream_enabled');
      socket.off('stream_disabled');
      socket.off('camera_started');
      socket.off('camera_stopped');
    };
  }, [socket]);

  const toggleStream = () => {
    if (streamEnabled) {
      socket.emit('disable_stream');
      setStreamEnabled(false);
    } else {
      socket.emit('enable_stream');
      setStreamEnabled(true);
    }
  };

  const toggleCamera = () => {
    if (cameraRunning) {
      socket.emit('stop_camera');
      setCameraRunning(false);
      setStreamEnabled(false); // Also disable stream when camera stops
    } else {
      socket.emit('start_camera');
      setCameraRunning(true);
    }
  };

  return (
    <div className="card">
      <h2>📹 Camera Control</h2>
      
      <div style={{ marginBottom: '15px', display: 'flex', gap: '10px' }}>
        <button 
          className={`button ${cameraRunning ? 'danger' : 'success'}`}
          onClick={toggleCamera}
          style={{ flex: 1 }}
        >
          {cameraRunning ? '🛑 Stop Camera' : '▶️ Start Camera'}
        </button>
        <button 
          className={`button ${streamEnabled ? 'danger' : 'success'}`}
          onClick={toggleStream}
          style={{ flex: 1 }}
          disabled={!cameraRunning}
        >
          {streamEnabled ? '👁️ Hide View' : '👁️ Show View'}
        </button>
      </div>

      <div style={{ 
        marginBottom: '15px', 
        padding: '10px', 
        background: cameraRunning ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)',
        borderRadius: '8px',
        textAlign: 'center',
        fontWeight: 'bold'
      }}>
        Camera Status: {cameraRunning ? '🟢 Running' : '🔴 Stopped'}
      </div>

      {streamEnabled && (
        <>
          <div style={{ 
            position: 'relative', 
            background: '#000', 
            borderRadius: '10px', 
            overflow: 'hidden',
            marginBottom: '10px'
          }}>
            <canvas
              ref={canvasRef}
              width={320}
              height={240}
              style={{
                width: '100%',
                height: 'auto',
                display: 'block'
              }}
            />
            {fps > 0 && (
              <div style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'rgba(0, 0, 0, 0.7)',
                color: '#4caf50',
                padding: '5px 10px',
                borderRadius: '5px',
                fontSize: '0.9em',
                fontWeight: 'bold'
              }}>
                {fps} FPS
              </div>
            )}
          </div>
          <p style={{ fontSize: '0.9em', opacity: 0.8, textAlign: 'center' }}>
            Camera feed is now streaming. Make sure your face is visible!
          </p>
        </>
      )}

      {!streamEnabled && (
        <div style={{ 
          padding: '40px', 
          textAlign: 'center', 
          background: 'rgba(0, 0, 0, 0.2)', 
          borderRadius: '10px',
          color: 'rgba(255, 255, 255, 0.6)'
        }}>
          Camera view is disabled. Click the button above to enable.
        </div>
      )}
    </div>
  );
}

export default CameraView;
